/* eslint-disable camelcase */
import moment, { Moment } from 'moment';
import React, { useState, useEffect } from 'react';

import swal from 'sweetalert';

import { formatCurrency } from '../../../../../utils/formatCurrency';
import DateSelectRange from '../../../../elements/DateSelectRange';
import Loader from '../../../../elements/Loader';
import Search from '../../../../elements/Search';
import SortButton, { SortOrder } from '../../../../elements/common/SortButton';
import TableHead from '../../../../elements/common/TableHead';
import Paginator from '../../../../elements/paginator';
import { TimeRangeName } from '../../../../helpers/ShowMetrics';
import { roundNumber } from '../../../../helpers/formUtils';
import axios from '../../../../services/MyAxios';
import {
    API_CASINO_DASHBOARD_DETAILED_BREAKDOWN,
    API_CASINO_DASHBOARD_DETAILED_BREAKDOWN_SUMMARY,
} from '../../Helpers/CrmApiUrls';
import GroupBySelect, { GroupBy } from '../Elements/GroupBySelect';

interface ReportData {
    name: string;
    creator_name?: string;
    ggr: number;
    ggr_margin: number;
    bets: number;
    wagered: number;
    rtp: number;
    won: number;
}

interface PaginatedData {
    last_page: number;
    current_page: number;
    data: Array<ReportData>;
}

export default function DetailedBreakdown() {
    const [isLoading, setIsLoading] = useState<Boolean>(true);
    const [range, setRange] = useState<TimeRangeName>('this-month');
    const [startDate, setStartDate] = useState<Moment>(moment().startOf('month'));
    const [endDate, setEndDate] = useState<Moment>(moment().endOf('month'));
    const [groupBy, setGroupBy] = useState<GroupBy>('name');
    const [detailedBreakDown, setDetailedBreakDown] = useState<PaginatedData>({
        last_page: 1,
        current_page: 1,
        data: [{ name: '', creator_name: undefined, ggr: 0, ggr_margin: 0, bets: 0, wagered: 0, rtp: 0, won: 0 }],
    });
    const [detailedBreakDownSummary, setDetailedBreakDownSummary] = useState({
        ggr: 0,
        ggr_margin: 0,
        bets: 0,
        wagered: 0,
        rtp: 0,
        won: 0,
    });
    const isGroupByGameName = groupBy === 'name';
    const [sortOrder, setSortOrder] = useState<SortOrder>(SortOrder.ASC);
    const [orderByColumn, setOrderByColumn] = useState<string>('casino_games.name');
    const [filter, setFilter] = useState<string>('');

    const updateOrderByOnGroupByChange = (newGroupBy: GroupBy): void => {
        setGroupBy(newGroupBy);
        setOrderByColumn(newGroupBy === 'name' ? 'casino_games.name' : 'casino_games.creator_name');
    };

    const getData = (page: number = 1): void => {
        setIsLoading(true);
        const formattedStartDate = startDate?.isValid() ? startDate.startOf('day').format('YYYY-MM-DD HH:mm:ss') : null;
        const formattedEndDate = endDate?.isValid() ? endDate.endOf('day').format('YYYY-MM-DD HH:mm:ss') : null;

        axios
            .get(API_CASINO_DASHBOARD_DETAILED_BREAKDOWN, {
                params: {
                    start: formattedStartDate,
                    end: formattedEndDate,
                    page,
                    groupBy,
                    orderByColumn,
                    sortOrder,
                    filter,
                },
            })
            .then((response) => {
                setDetailedBreakDown(response.data);
            })
            .catch(() => {
                swal({
                    title: 'Error',
                    text: 'We have encountered an error.',
                    icon: 'error',
                });
            });

        setIsLoading(false);
    };

    const getSummary = (): void => {
        setIsLoading(true);

        const formattedStartDate = startDate?.isValid() ? startDate.startOf('day').format('YYYY-MM-DD HH:mm:ss') : null;
        const formattedEndDate = endDate?.isValid() ? endDate.endOf('day').format('YYYY-MM-DD HH:mm:ss') : null;

        axios
            .get(API_CASINO_DASHBOARD_DETAILED_BREAKDOWN_SUMMARY, {
                params: {
                    start: formattedStartDate,
                    end: formattedEndDate,
                },
            })
            .then((response) => {
                setDetailedBreakDownSummary(response.data);
            })
            .catch(() => {
                swal({
                    title: 'Error',
                    text: 'We have encountered an error.',
                    icon: 'error',
                });
            });

        setIsLoading(false);
    };

    const hidePreviousOrders = (column: string): void => {
        const containers = document.querySelectorAll(
            `.sort-header-container[data-column]:not([data-column="${column}"])`
        );
        containers.forEach((container) => {
            const buttons = container.querySelectorAll('.sort-header-order-button');
            buttons.forEach((button: HTMLElement) => {
                button.style.display = 'none';
            });
        });
    };

    const onSortChange = (column: string, order: SortOrder): void => {
        setOrderByColumn(column);
        setSortOrder(order);
        hidePreviousOrders(column);
    };

    useEffect((): void => {
        getData();
        getSummary();
    }, []);

    useEffect((): void => {
        getSummary();
    }, [startDate, endDate, filter]);

    useEffect((): void => {
        if (!endDate) {
            return;
        }

        getData();
    }, [startDate, endDate, groupBy, orderByColumn, sortOrder, filter]);

    const getContent = () => {
        return (
            <div className="dashboard-block">
                <div className="dashboard-block-header">
                    <div className="dashboard-block-header-title">Detailed breakdown</div>
                    <div className="dashboard-block-date-filter d-flex">
                        <Search filterTable={setFilter} />
                        <GroupBySelect groupBy={groupBy} setGroupBy={updateOrderByOnGroupByChange} />
                        <DateSelectRange
                            containerClasses="d-flex"
                            range={range}
                            setRange={setRange}
                            startDate={startDate}
                            setStartDate={setStartDate}
                            endDate={endDate}
                            setEndDate={setEndDate}
                        />
                    </div>
                </div>
                <div className="dashboard-block-data">
                    <div className="table-responsive">
                        <table className="table model-list">
                            <TableHead>
                                <SortButton
                                    column={isGroupByGameName ? 'casino_games.name' : 'casino_games.creator_name'}
                                    label={isGroupByGameName ? 'Game' : 'Provider'}
                                    onSortChange={onSortChange}
                                    sortParams={{}}
                                />
                                <SortButton column="bets" label="Bets" onSortChange={onSortChange} sortParams={{}} />
                                <SortButton
                                    column="wagered"
                                    label="Wagered"
                                    onSortChange={onSortChange}
                                    sortParams={{}}
                                />
                                <SortButton column="won" label="Won" onSortChange={onSortChange} sortParams={{}} />
                                <SortButton
                                    column="rtp"
                                    label="RTP"
                                    onSortChange={onSortChange}
                                    definition="Return to player. Formula: amount of wins/amount of bets"
                                    sortParams={{}}
                                />
                                <SortButton
                                    column="ggr"
                                    label="GGR"
                                    onSortChange={onSortChange}
                                    definition="Gross game revenue"
                                    sortParams={{}}
                                />
                                <SortButton
                                    column="ggr_margin"
                                    label="GGR margin"
                                    onSortChange={onSortChange}
                                    definition="Formula: GGR/wagered"
                                    sortParams={{}}
                                />
                            </TableHead>
                            <tbody>
                                {detailedBreakDown.data.map((reportData) => (
                                    <tr key={reportData.creator_name ?? reportData.name}>
                                        <td>{reportData.creator_name ?? reportData.name}</td>
                                        <td>{reportData.bets}</td>
                                        <td>{formatCurrency(reportData.wagered, 2)}</td>
                                        <td>{formatCurrency(reportData.won, 2)}</td>
                                        <td>{roundNumber(reportData.rtp * 100, 2)}%</td>
                                        <td>{formatCurrency(reportData.ggr, 2)}</td>
                                        <td>{roundNumber(reportData.ggr_margin * 100, 2)}%</td>
                                    </tr>
                                ))}
                                <tr key="summary" className="table-summary">
                                    <td>Summary</td>
                                    <td>{detailedBreakDownSummary.bets}</td>
                                    <td>{formatCurrency(detailedBreakDownSummary.wagered, 2)}</td>
                                    <td>{formatCurrency(detailedBreakDownSummary.won, 2)}</td>
                                    <td>{roundNumber(detailedBreakDownSummary.rtp * 100, 2)}%</td>
                                    <td>{formatCurrency(detailedBreakDownSummary.ggr, 2)}</td>
                                    <td>{roundNumber(detailedBreakDownSummary.ggr_margin * 100, 2)}%</td>
                                </tr>
                            </tbody>
                        </table>
                        <Paginator
                            hideIf={detailedBreakDown.last_page === 1}
                            key="paginator"
                            currentPage={detailedBreakDown.current_page}
                            lastPage={detailedBreakDown.last_page}
                            onPageChange={getData}
                        />
                    </div>
                </div>
            </div>
        );
    };

    return <Loader loading={isLoading} content={getContent} />;
}
